import { autorun, observe, reaction } from '@rehaje/reactive'
import { isArray, keys, uid } from '@rehaje/shared'

import { FatalAddEffectError } from '../errors'
import { finalization } from '../finalization'
import { EventTarget } from './EventTarget'

import type { EffectOptions } from '../types'
import type {
  ObserveOptions,
  Observer,
  ReactionOptions,
  Subscriber
} from '@rehaje/reactive'
import type {
  Callback,
  Disposer,
  DisposerLikes,
  Effect,
  TRecord
} from '@rehaje/shared'

/**
 * 生命周期基类，为 Rehaje 所有树节点提供生命周期的基础管理能力
 */
export abstract class LifeCycle extends EventTarget {
  abstract __r_debug_id: string

  __r_effect_maps?: Record<string, Effect>

  __r_effect_queue?: Effect[]

  __r_immediate_effects?: Effect[]

  /**
   * 是否初始化完成
   */
  __r_initialized?: boolean

  /**
   * 是否已挂载
   */
  __r_mounted?: boolean

  /**
   * 是否已卸载
   */
  __r_unmounted?: boolean

  __r_addEffect(effect: Effect, priority?: 'high' | 'normal') {
    const target: LifeCycle = this.__r_getEventTarget()
    if (target.__r_effect_queue) {
      if (priority === 'high') target.__r_effect_queue.unshift(effect)
      else target.__r_effect_queue.push(effect)
    } else {
      target.__r_effect_queue = [effect]
    }
  }

  __r_deleteEffect(key: string) {
    const target: LifeCycle = this.__r_getEventTarget()
    if (target.__r_effect_maps) {
      delete target.__r_effect_maps[key]
    }
  }

  __r_dispose(disposer?: DisposerLikes) {
    if (!disposer) return
    if (isArray(disposer)) {
      disposer.forEach((disposer) => this.__r_dispose(disposer))
      return
    }
    if (typeof disposer === 'function') {
      disposer()
    }
  }

  __r_disposeEffect(effect?: Effect) {
    if (!effect) return
    this.__r_dispose(effect.__r_disposers)
    effect.__r_disposers = undefined
  }

  __r_removeEffect(effect: Effect) {
    const target: LifeCycle = this.__r_getEventTarget()
    if (target.__r_immediate_effects) {
      const index = target.__r_immediate_effects.indexOf(effect)
      if (index !== -1) target.__r_immediate_effects.splice(index, 1)
    }
    if (target.__r_effect_queue) {
      const index = target.__r_effect_queue.indexOf(effect)
      if (index !== -1) target.__r_effect_queue.splice(index, 1)
    }
  }

  __r_runEffect(effect: Effect, immediately?: boolean): void {
    if (immediately) {
      if (this.__r_immediate_effects) {
        this.__r_immediate_effects.push(effect)
      } else {
        this.__r_immediate_effects = [effect]
      }
    } else {
      if (this.__r_immediate_effects?.includes(effect)) return
    }
    const disposers = effect()
    if (!disposers) return
    if (!effect.__r_disposers) {
      effect.__r_disposers = disposers
    } else if (isArray(effect.__r_disposers)) {
      effect.__r_disposers = effect.__r_disposers.concat(disposers)
    } else {
      effect.__r_disposers = [effect.__r_disposers].concat(disposers as any)
    }
  }

  __r_setEffect(key: string, effect: Effect) {
    const target: LifeCycle = this.__r_getEventTarget()
    if (target.__r_effect_maps) {
      target.__r_disposeEffect(target.__r_effect_maps[key])
      target.__r_effect_maps[key] = effect
    } else {
      target.__r_effect_maps = { [key]: effect }
    }
  }

  /**
   * 添加响应式自动执行器,只能在初始化期间使用，类初始化完成之后只能使用 setAutorun
   * @param tracker
   */
  addAutorun(tracker: Callback): Disposer {
    if (this.__r_initialized)
      throw new FatalAddEffectError('addAutorun', 'setAutorun')
    return this.addEffect(
      () => autorun(tracker, { onError: (e) => this.raise(e) }),
      {
        immediately: true
      }
    )
  }

  /**
   * 添加副作用,只能在初始化期间使用，类初始化完成之后只能使用 setEffect
   * @param effect
   * @param options
   * @returns
   */
  addEffect(effect: Effect, options?: EffectOptions): Disposer {
    if (this.__r_initialized)
      throw new FatalAddEffectError('addEffect', 'setEffect')
    const target: LifeCycle = this.__r_getEventTarget()
    const isImmediate = target.__r_mounted || options?.immediately
    if (isImmediate) target.__r_runEffect(effect, true)
    target.__r_addEffect(effect, options?.priority)
    return () => {
      target.__r_disposeEffect(effect)
      target.__r_removeEffect(effect)
    }
  }

  /**
   * 添加深度监听器(可监听任意响应式对象变化),只能在初始化期间使用，类初始化完成之后只能使用 setObserve
   * @param target
   * @param observer
   * @param options
   */
  addObserve(
    target: any,
    observer: Observer,
    options?: ObserveOptions & TRecord
  ): Disposer {
    if (this.__r_initialized)
      throw new FatalAddEffectError('addObserve', 'setObserve')
    return this.addEffect(() => observe(target, observer, options), {
      immediately: true,
      priority: 'high'
    })
  }

  /**
   * 添加读写分离的响应式自动执行器(可定制脏检查逻辑),只能在初始化期间使用，类初始化完成之后只能使用 setReaction
   * @param tracker
   * @param subscriber
   * @param options
   */
  addReaction<T = any>(
    tracker: Callback<T>,
    subscriber?: Subscriber<T, T>,
    options?: ReactionOptions<T, T>
  ): Disposer
  addReaction<T = any>(
    tracker: Callback<T>,
    subscriber?: Subscriber<T, T | undefined>,
    options?: ReactionOptions<T, T | undefined> & { fireImmediately: true }
  ): Disposer
  addReaction(
    tracker: Callback,
    subscriber?: Subscriber,
    options?: ReactionOptions
  ): Disposer {
    if (this.__r_initialized)
      throw new FatalAddEffectError('addReaction', 'setReaction')
    return this.addEffect(
      () => {
        options ??= {}
        options.onError ??= (e) => this.raise(e)
        return reaction(tracker, subscriber, options)
      },
      { immediately: true }
    )
  }

  /**
   * 添加渲染副作用，和普通的副作用不同，只有在 Store 挂载和卸载时触发
   * 这是为了将 globalThis.addEventListener 和 setTimeout 相关的副作用和渲染周期绑定在一起，防止内存泄漏
   * 否则，在 React StrictMode 下会出现仅创建不销毁的情况，导致内存泄漏
   */
  addRenderEffect(effect: Effect, options?: EffectOptions): Disposer {
    const effectKey = `renderEffect:${uid()}`
    const disposer = this.getStore().setEffect(effectKey, effect, options)
    finalization.register(this, new WeakRef(disposer))
    return disposer
  }

  /**
   * 清理副作用
   */
  dispose(removeEffect?: boolean): void {
    const target = this.__r_getEventTarget() as LifeCycle
    if (target.__r_unmounted === true) return
    if (target.__r_effect_queue) {
      for (const effect of target.__r_effect_queue.slice()) {
        target.__r_disposeEffect(effect)
        if (removeEffect) target.__r_removeEffect(effect)
      }
    }
    if (target.__r_effect_maps) {
      for (const key of keys(target.__r_effect_maps)) {
        target.__r_disposeEffect(target.__r_effect_maps[key])
        if (removeEffect) target.__r_deleteEffect(key)
      }
    }
    target.__r_immediate_effects = undefined
    target.__r_mounted = false
    target.__r_unmounted = true
  }

  /**
   *
   */
  effect(): Disposer {
    const target: LifeCycle = this.__r_getEventTarget()
    if (target.__r_mounted !== true) {
      if (target.__r_effect_queue)
        for (const effect of target.__r_effect_queue) target.__r_runEffect(effect)
      if (target.__r_effect_maps) {
        for (const key of keys(target.__r_effect_maps)) {
          target.__r_runEffect(target.__r_effect_maps[key])
        }
      }
      target.__r_mounted = true
      target.__r_unmounted = false
    }
    return () => target.dispose()
  }

  /**
   * 节点已经初始化
   */
  isInitialized() {
    return this.__r_getEventTarget().__r_initialized ?? false
  }

  /**
   * 节点是否已挂载
   */
  isMounted(): boolean {
    return this.__r_getEventTarget().__r_mounted ?? false
  }

  /**
   * 设置响应式自动执行器,重复调用会自动销毁上一个同 key autorun
   * @param key
   * @param tracker
   */
  setAutorun(key: string, tracker: Callback): Disposer {
    return this.setEffect(
      key,
      () => autorun(tracker, { onError: (e) => this.raise(e) }),
      {
        immediately: true
      }
    )
  }

  /**
   * 设置副作用,可以重复调用，重复调用会自动销毁上一个同 key effect
   * @param key
   * @param effect
   * @param options
   * @returns
   */
  setEffect(key: string, effect: Effect, options?: EffectOptions): Disposer {
    const target: LifeCycle = this.__r_getEventTarget()
    const isImmediate = target.__r_mounted || options?.immediately
    if (isImmediate) target.__r_runEffect(effect, true)
    target.__r_setEffect(key, effect)
    return () => {
      target.__r_disposeEffect(effect)
      target.__r_removeEffect(effect)
      target.__r_deleteEffect(key)
    }
  }

  /**
   * 添加深度监听器(可监听任意响应式对象变化),可以重复调用，重复调用会自动销毁上一个同 key observe
   * @param target
   * @param observer
   * @param options
   */
  setObserve(
    key: string,
    target: any,
    observer: Observer,
    options?: ObserveOptions & TRecord
  ): Disposer {
    return this.setEffect(key, () => observe(target, observer, options), {
      immediately: true,
      priority: 'high'
    })
  }

  /**
   * 设置读写分离的响应式自动执行器(可定制脏检查逻辑),可以重复调用，重复调用会自动销毁上一个同 key reaction
   * @param key
   * @param tracker
   * @param subscriber
   * @param options
   */
  setReaction<T = any>(
    key: string,
    tracker: Callback<T>,
    subscriber?: Subscriber<T, T>,
    options?: ReactionOptions<T, T>
  ): Disposer
  setReaction<T = any>(
    key: string,
    tracker: Callback<T>,
    subscriber?: Subscriber<T, T | undefined>,
    options?: ReactionOptions<T, T | undefined> & { fireImmediately: true }
  ): Disposer
  setReaction(
    key: string,
    tracker: Callback,
    subscriber?: Subscriber,
    options?: ReactionOptions
  ): Disposer
  setReaction(
    key: string,
    tracker: Callback,
    subscriber?: Subscriber,
    options?: ReactionOptions
  ): Disposer {
    return this.setEffect(
      key,
      () => {
        options ??= {}
        options.onError ??= (e) => this.raise(e)
        return reaction(tracker, subscriber, options)
      },
      { immediately: true }
    )
  }
}
